import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';

const ServiceHero = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url("https://images.unsplash.com/photo-1552889543-d5d6e5a98fb3?w=1600&auto=format&fit=crop")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  height: '50vh',
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.common.white,
  [theme.breakpoints.down('md')]: {
    height: '40vh',
  },
}));

const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)',
  },
}));

const services = [
  {
    title: 'Airport Transfers',
    image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&auto=format&fit=crop',
    description: 'Premium airport transfer service in our luxury V-Class. We monitor your flight and adjust pickup time accordingly. Available for all major UK airports.',
    features: [
      'Flight monitoring & adjustments',
      'Meet & Greet service',
      'Complimentary 15 minutes waiting time',
      'Luggage assistance',
      'Spacious comfort for up to 5 passengers',
      'Refreshments provided',
    ],
  },
  {
    title: 'Corporate Travel',
    image: 'https://images.unsplash.com/photo-1519003722824-194d4455a60c?w=800&auto=format&fit=crop',
    description: 'Executive transportation in our Mercedes V-Class. Perfect for business meetings, conferences, and corporate events with space to work or relax.',
    features: [
      'Professional chauffeurs',
      'Mobile office environment',
      'Wi-Fi enabled vehicles',
      'Privacy screens',
      'Multiple seating configurations',
      'Corporate accounts available',
    ],
  },
  {
    title: 'Special Events',
    image: 'https://images.unsplash.com/photo-1563720360172-67b8f3dce741?w=800&auto=format&fit=crop',
    description: 'Make your special occasions memorable with our luxury V-Class service. Perfect for weddings, celebrations, and VIP events.',
    features: [
      'Luxury V-Class vehicles',
      'Red carpet service',
      'Complimentary champagne',
      'Flexible duration',
      'Special event coordination',
    ],
  },
  {
    title: 'Hourly Hire',
    image: 'https://images.unsplash.com/photo-1552889543-d5d6e5a98fb3?w=800&auto=format&fit=crop',
    description: 'Flexible hourly hire of our V-Class with chauffeur. Perfect for business days, shopping trips, or when you need a luxury vehicle at your disposal.',
    features: [
      'Dedicated chauffeur',
      'Multiple stops',
      'Wait and return service',
      'Central London coverage',
      'Short notice availability',
    ],
  },
];

const ServicesPage = () => {
  return (
    <Box>
      <ServiceHero>
        <Container>
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              lineHeight: { xs: 1.2, md: 1.1 },
            }}
          >
            Luxury V-Class Services
          </Typography>
          <Typography 
            variant="h5"
            sx={{
              maxWidth: '600px',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              lineHeight: { xs: 1.4, md: 1.5 },
              opacity: 0.9,
            }}
          >
            Every journey is a statement of luxury and sophistication
          </Typography>
        </Container>
      </ServiceHero>

      <Container sx={{ py: { xs: 6, md: 12 } }}>
        <Typography 
          variant="h3" 
          component="h2" 
          textAlign="center" 
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: { xs: 1, md: 2 },
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.25rem' },
            lineHeight: 1.2,
          }}
        >
          Our Premium Services
        </Typography>
        <Typography 
          variant="h6" 
          textAlign="center" 
          color="text.secondary" 
          sx={{ 
            mb: { xs: 4, md: 8 },
            maxWidth: '800px',
            mx: 'auto',
            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
            lineHeight: { xs: 1.5, md: 1.6 },
            opacity: 0.85,
          }}
        >
          Experience the perfect blend of luxury, comfort, and professional service
        </Typography>

        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid item xs={12} md={6} key={index}>
              <ServiceCard>
                <CardMedia
                  component="img"
                  height="300"
                  image={service.image}
                  alt={service.title}
                  sx={{ 
                    objectFit: 'cover',
                  }}
                />
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    gutterBottom
                    sx={{ 
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    {service.title}
                  </Typography>
                  <Typography 
                    color="text.secondary" 
                    paragraph
                    sx={{ mb: 3 }}
                  >
                    {service.description}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    {service.features.map((feature, idx) => (
                      <Typography 
                        key={idx} 
                        component="div" 
                        sx={{ 
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          '&:before': {
                            content: '"•"',
                            marginRight: 1,
                            color: 'primary.main',
                            fontWeight: 'bold',
                          }
                        }}
                      >
                        {feature}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
                <Box sx={{ p: 3, pt: 0 }}>
                  <Button
                    component={RouterLink}
                    to="/booking"
                    variant="contained"
                    fullWidth
                    sx={{
                      bgcolor: 'common.black',
                      color: 'common.white',
                      py: 1.5,
                      '&:hover': {
                        bgcolor: 'grey.900',
                      },
                    }}
                  >
                    Book Now
                  </Button>
                </Box>
              </ServiceCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ServicesPage; 